/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { baseStyles } from './themes/PictoChartStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { generateElementCoords, getFlowingInfo } from './pictoChartLayoutUtils';
import { useSelection } from '../hooks/PRIVATE_useVisSelection';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { getItemInfo, getDatatipContent, getItemProps, getTotalCount } from './pictoChartUtils';
import { getPictoChartNavUtils } from './pictoChartNavUtils';
import { useEvents } from './useEvents';
import { usePictoChartDatatip } from './usePictoChartDatatip';
import { useItemFocus } from '../hooks/PRIVATE_useVisEvents';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { PictoChartProps, PictoChartItemData } from './pictoChart.type';
import { PictoChartElement } from './PictoChartElement';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '#utils/UNSAFE_stringUtils';

type PictoChartWithDimensionsProps<K, D> = Omit<PictoChartProps<K, D>, 'width' | 'height'> & {
  width: number;
  height: number;
  isFlowingWidth: boolean;
  isFlowingHeight: boolean;
};

export function PictoChartWithDimensions<
  K extends string | number,
  D extends PictoChartItemData<K> = PictoChartItemData<K>
>({
  width,
  height,
  columnWidth,
  rowHeight,
  items,
  columnCount,
  rowCount,
  layout,
  layoutOrigin = 'topStart',
  selectionMode = 'none',
  highlightedIds = [],
  selectedIds = [],
  drilling = 'off',
  onItemDrill,
  onItemHover,
  isFlowingWidth,
  isFlowingHeight,
  onItemFocus,
  ...props
}: PictoChartWithDimensionsProps<K, D>) {
  const hasData = items.length !== 0;
  const totalCount = getTotalCount(items);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const supportsSelection = !!(selectionMode && selectionMode !== 'none');
  const supportsDrill = onItemDrill != null;
  const isReadOnly =
    !supportsSelection && !supportsDrill && onItemHover === null && onItemFocus === null;
  const testIdProps = useTestId(props.testId);
  const idToDataMap = new Map(items.map((obj) => [obj.id, obj]));

  const { getItem, getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getPictoChartNavUtils<
    K,
    D
  >(items);

  const { touchResponse, touchResponseStyle } = useVisTouchResponse({ type: 'touchStart' });

  const { hoveredItemInfo, focusedItemInfo, activeId, eventsProps } = useEvents<K, D>(
    touchResponse,
    !isReadOnly && hasData,
    selectionMode,
    getDetailFromInfo,
    getPrevItemInfo,
    getNextItemInfo,
    drilling,
    onItemDrill,
    onItemHover,
    onItemFocus
  );

  const idExtracter = (event: Event) => {
    // return focused item for keyboard event.
    // eventsProps should have already updated the focused item
    const info =
      event.type === 'keyup' ? focusedItemInfo : getItemInfo(event.target as HTMLElement);
    return getDetailFromInfo(info).id;
  };

  const { selectionContent, selectionProps } = useSelection<K, D>({
    idExtracter,
    isDrillEnabled: drilling != 'off',
    selection: selectedIds,
    selectionMode: selectionMode,
    getDataById: (id) => idToDataMap.get(id),
    onChange: props.onSelectionChange
  });

  const rectangleWidth = columnWidth && parseFloat(columnWidth);
  const rectangleHeight = rowHeight && parseFloat(rowHeight);
  const pictoStyleClasses = classNames([baseStyles]);
  const { focusedItemRef } = useItemFocus(focusedItemInfo);
  const { content, borderColor } = getDatatipContent(
    items,
    getItem,
    totalCount,
    focusedItemInfo,
    hoveredItemInfo,
    props.datatip,
    translations
  );
  const { calcColCount, calcRowCount, calcColWidth, calcRowHeight, gridGap } = getFlowingInfo<K, D>(
    items,
    layout === 'vertical',
    width,
    height,
    isFlowingWidth,
    isFlowingHeight,
    columnCount,
    rowCount,
    rectangleWidth,
    rectangleHeight
  );

  const {
    datatipContent,
    datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
  } = usePictoChartDatatip({
    touchResponse,
    content,
    width,
    borderColor,
    focusedItemRef,
    focusedItemInfo
  });

  if (!calcColCount)
    // Nothing to render.
    return null;

  const elements = generateElementCoords(
    items,
    calcRowCount,
    calcColCount,
    layoutOrigin,
    layout === 'vertical'
  );

  const mergedEventProps = mergeProps(eventsProps, datatipProps, selectionProps);
  return (
    <div
      tabIndex={0}
      aria-label={props['aria-label']}
      aria-describedby={merge([props['aria-describedby'], datatipAriaDescribedby])}
      aria-labelledby={props['aria-labelledby']}
      aria-activedescendant={activeId}
      role="application"
      style={{
        width: isFlowingWidth ? 'max-content' : `${width}px`,
        height: isFlowingHeight ? 'max-content' : `${height}px`,
        gridTemplateRows: `repeat(${calcRowCount}, ${calcRowHeight}px)`,
        gridTemplateColumns: `repeat(${calcColCount}, ${calcColWidth}px)`,
        gap: `${gridGap}px`,
        ...touchResponseStyle
      }}
      class={pictoStyleClasses}
      {...testIdProps}
      {...mergedEventProps}>
      {elements.map((elt) => {
        const isFocusedItem = focusedItemInfo.idx === elt.itemIdx;
        const itemProps = getItemProps(
          elt.item,
          elt.itemIdx,
          elt.countIdx,
          isReadOnly,
          hoveredItemInfo,
          selectedIds,
          focusedItemInfo,
          highlightedIds,
          activeId
        );

        const context = {
          data: elt.item,
          state: {
            selected: itemProps.isSelected,
            focused: itemProps.isFocused,
            hovered: itemProps.isCurrent
          }
        };

        return (
          <PictoChartElement<K, D>
            focusedItemRef={isFocusedItem ? focusedItemRef : undefined}
            row={elt.row}
            column={elt.col}
            supportsSelection={supportsSelection}
            supportsDrill={supportsDrill}
            translations={translations}
            totalCount={totalCount}
            {...itemProps}>
            {props.children(context)}
          </PictoChartElement>
        );
      })}
      {datatipContent}
      {selectionContent}
    </div>
  );
}
